// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import { useTranslation } from 'react-i18next';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import { get } from 'lodash';
import { logException } from './../../lib/ApplicationInsights';

import {
    PrimaryButton,
    Spinner,
    SpinnerSize,
} from 'office-ui-fabric-react';
import history from 'src/history';

import RegisterDevice from './RegisterDevice';
import RegistrationDetail from './RegistrationDetail';
import Styles from './registerDevice.module.scss';

import { LoadingState } from './../../store/actionStatus';
import {
    GET_JARVIS_DATA,
    GET_REGISTER_INFO_DATA,
    customerInfoSelector,
    reActivateOrderStatusSelector,
    offerReasonsSelector,
    PURCHASE_ON_DEMAND_ORDER,
    purchaseOnDemandOrder,
    secondSRCreationSelector,
} from './../../store/orderCreation';
import { statusSelector } from './../../components/ServiceRequestAndDeviceLookup/selector';
import ErrorMessage from './../../components/ErrorMessage';
import { TranslationDetails, translateMessage } from './../../helpers/errorMessageInterpreter';

import { logPageAction, sessionId } from 'src/lib/ApplicationInsights';
import CorrelationVector from 'src/lib/CorrelationVector';

export default function Registration() {
    const { t } = useTranslation();

    const registerStatus = useSelector((state: any) => state && state.actionStatus[GET_REGISTER_INFO_DATA] || {});
    const jarvisStatus = useSelector((state: any) => state && state.actionStatus[GET_JARVIS_DATA] || {});
    const customerInfo = useSelector(state => customerInfoSelector(state));
    const reActivateOrder = useSelector(state => reActivateOrderStatusSelector(state));
    const offerReasonForNotRepairng = useSelector(state => offerReasonsSelector(state));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));

    const repairComments = offerReasonForNotRepairng?.repairComments;
    if ((reActivateOrder && typeof (repairComments) !== 'undefined' && repairComments !== '') || secondSRCreation) {
        return <UpdateOrderWithReasons />;
    }
    if (reActivateOrder) {
        return <RegistrationDetail />;
    }
    return <RegistrationItem registerStatus={registerStatus} jarvisStatus={jarvisStatus} customerInfo={customerInfo} t={t} />;
}
export function UpdateOrderWithReasons() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const callStatus = useSelector(state => statusSelector(state)(PURCHASE_ON_DEMAND_ORDER));
    const secondSRCreation = useSelector(state => secondSRCreationSelector(state));
    const offerReasonForNotRepairng = useSelector(state => offerReasonsSelector(state));
    const isLoading = callStatus.status === LoadingState.started;
    const orderIsError = callStatus.status === LoadingState.rejected;
    let onDemandOrderMessage = '';
    if (callStatus) {
        const checkoutOrderRequestErrorMessage = get(callStatus, 'error.data.description') || get(callStatus, 'error.data.ErrorMessage');
        const translatedCheckoutRequestErrorMessage: TranslationDetails | undefined = translateMessage(checkoutOrderRequestErrorMessage);
        if (translatedCheckoutRequestErrorMessage) {
            onDemandOrderMessage = t(translatedCheckoutRequestErrorMessage.key, translatedCheckoutRequestErrorMessage.values);
        }
    }

    function onSubmit() {
        logPageAction('Update Order With Reasons');
        if(secondSRCreation){
            dispatch(purchaseOnDemandOrder({
                    secondSRCreation,
                    isOnDemandCheckOut:false,
                    isOnSiteCheckOut: true,
                    claimOrderType: 'onsite',
                    dnrReason: offerReasonForNotRepairng?.reason,
                    dnrComments: offerReasonForNotRepairng?.repairComments,
                }));
        } 
        else {
            dispatch(purchaseOnDemandOrder({ isOnDemandCheckOut: false, dnrReason: offerReasonForNotRepairng?.reason, dnrComments: offerReasonForNotRepairng?.repairComments}));
            history.push('/device/orderConfirmation');
        }
    }

    return (
        <div>
            <div className="ms-Grid-row">
                <div className={clsx('ms-Grid-col', 'ms-sm9')} />
                <div className={clsx('ms-Grid-col', 'ms-sm3')}>
                    <div className={Styles.buttonContainer}>
                        <div className={Styles.buttonAndSpinnerContainer}>
                            <PrimaryButton
                                allowDisabledFocus={true}
                                ariaLabel={t('update_order')}
                                className={Styles.submit}
                                disabled={isLoading}
                                onClick={onSubmit}
                                text={secondSRCreation ? t('continue') : t('update_order')}
                            />
                            {isLoading &&
                                <Spinner
                                    className={Styles.spinner}
                                    size={SpinnerSize.medium}
                                />
                            }
                        </div>
                    </div>
                </div>
            </div>
            <ErrorMessage
                contactSupport={orderIsError}
                message={orderIsError ? onDemandOrderMessage : undefined}
                traceData={callStatus}
            />
        </div>
    );
}

export function RegistrationItem({ registerStatus, jarvisStatus, customerInfo, t }: { registerStatus: any, jarvisStatus: any, customerInfo: any, t: any }) {
    const { error } = jarvisStatus;
    const statusCode = error && error.status;
    switch (registerStatus.status) {
        case LoadingState.success:
            return <RegistrationDetail />;
        case LoadingState.rejected:
            let errorMessage = '';
            let displayOnlyOperationId = false;
            if (jarvisStatus.status === LoadingState.rejected) {
                if (statusCode === 404) {
                    errorMessage = t('registration_call_failed_check_email');
                } else {
                    errorMessage = t('something_went_wrong', { sessionId, operationId: CorrelationVector.getPreviousCV() });
                }
                logException(CorrelationVector.getPreviousCV(), error, errorMessage, false);
            } else {
                const { data = {}, message } = registerStatus.error;
                const { details = [], description = '', message:internalError } = data;
                errorMessage = (Array.isArray(details) && details.length && details[0])
                    || (message && t(message) || message)
                    || description
                    || t('unknown_registration_error');
                
                if( internalError && t("device_registration_progress", { lng: 'en' }).toLowerCase().startsWith(internalError.toLowerCase()) ) 
                {
                    errorMessage = t("device_registration_progress");
                    displayOnlyOperationId = true;
                }
            }
            return <RegisterDevice editedInfo={customerInfo} errorMessage={errorMessage} displayOnlyOperationId ={displayOnlyOperationId}/>;
        default:
            return <RegisterDevice editedInfo={customerInfo} />;
    }
}